<script setup lang="ts">
import { useHomeStore } from '@/stores/app/home.store'
import { computed, onMounted, ref, watch } from 'vue'
import LabelBase from '@/components/label/LabelBase.vue'
import type { Part, Sale, Stock } from '@/types/model'
import CardInfo from '@/components/card/CardInfo.vue'
import CardPart from '@/components/card/CardPart.vue'
import { useRouter } from 'vue-router'
import { useModalStore } from '@/stores/modal.store.ts'
import { colorStockType } from '@/helpers/stock.helper.ts'
import CardError from '../card/CardError.vue'
import ModalLoading from '../modal/ModalLoading.vue'
import { LoadStatus } from '@/enums/app'
import { handleError } from '@/helpers/app.helper.ts'

// props and emits
const props = defineProps<{
  text: string
}>()

// router and route
const router = useRouter()

// stores
const { loadHome } = useHomeStore()
const { closeModal } = useModalStore()

// refs
const loadStatus = ref<LoadStatus>(LoadStatus.None)
const parts = ref<Part[]>([])
const sales = ref<Sale[]>([])
const stocks = ref<Stock[]>([])

// computed
const searchText = computed<string>(() => encodeURIComponent(props.text))
const isEmpty = computed<boolean>(() => {
  const notEmptyParts: boolean = parts.value.length > 0
  const notEmptySales: boolean = sales.value.length > 0
  const notEmptyStocks: boolean = stocks.value.length > 0

  return !notEmptySales && !notEmptyParts && !notEmptyStocks
})

// methods
const jumpToSale = async (id: number) => {
  await router.push({
    name: 'sale-edit',
    params: {
      id,
    },
  })

  // tutup
  closeModal()
}

const jumpToStock = async (id: number) => {
  await router.push({
    name: 'stock-edit',
    params: {
      id,
    },
  })

  // tutup
  closeModal()
}

const jumpToPart = async (id: number) => {
  await router.push({
    name: 'part-edit',
    params: {
      id,
    },
  })

  // tutup
  closeModal()
}

// lifecycle
onMounted(async () => {
  loadStatus.value = LoadStatus.Checking

  if (searchText.value) {
    try {
      const query: string = '?perPage=5&search=' + searchText.value
      const res = await loadHome(query)

      if (res.data.status === 'success') {
        parts.value = res.data.search.parts.data.data as Part[]
        sales.value = res.data.search.sales.data.data as Sale[]
        stocks.value = res.data.search.stocks.data.data as Stock[]

        loadStatus.value =
          parts.value.length > 0 || sales.value.length > 0 || stocks.value.length > 0
            ? LoadStatus.Found
            : LoadStatus.NotFound
      } else {
        loadStatus.value = LoadStatus.Error
      }
    } catch (e: unknown) {
      const status = handleError(e)
      loadStatus.value = status === 404 ? LoadStatus.NotFound : LoadStatus.Error
    }
  }
})

watch(
  () => isEmpty.value,
  (newVal) => {
    loadStatus.value = newVal === true ? LoadStatus.NotFound : LoadStatus.Found
  },
)
</script>

<template>
  <div class="h-88 overflow-y-auto">
    <modal-loading v-if="loadStatus === LoadStatus.Checking"></modal-loading>

    <div v-if="loadStatus === LoadStatus.Found">
      <card-info v-if="parts.length > 0" title="parts" desc="hasil pencarian suku cadang">
        <div class="flex flex-col w-full space-y-2">
          <div
            v-for="(part, i) in parts"
            :key="i"
            @click="jumpToPart(part.id ?? 0)"
            class="p-2 hover:bg-blue-500/25 rounded-xl transition-colors duration-150 cursor-pointer"
          >
            <card-part :part="part" :detail="true"></card-part>
          </div>
          <div class="text-center">
            <label-base size="xs" color="stone"
              >Hanya menampilkan 5 data saja, silakan eja dengan benar</label-base
            >
          </div>
        </div>
      </card-info>
      <card-info v-if="sales.length > 0" title="sales" desc="hasil pencarian penjualan">
        <div class="flex flex-col w-full space-y-2">
          <div
            v-for="(sale, i) in sales"
            :key="i"
            @click="jumpToSale(sale.id ?? 0)"
            class="p-2 hover:bg-blue-500/25 rounded-xl transition-colors duration-150 cursor-pointer"
          >
            <div>
              <label-base size="sm" color="stone" class="block font-mono">{{
                sale.ref
              }}</label-base>
              <label-base class="block font-bold font-mono">{{ sale.name }}</label-base>
              <label-base size="sm" :color="sale.marketplaces?.color ?? 'stone'" class="block"
                >{{ sale.marketplaces?.name ?? 'No Marketplace' }}
              </label-base>
            </div>
          </div>
          <div class="text-center">
            <label-base size="xs" color="stone"
              >Hanya menampilkan 5 data saja, silakan eja dengan benar</label-base
            >
          </div>
        </div>
      </card-info>
      <card-info v-if="stocks.length > 0" title="stocks" desc="hasil pencarian stock">
        <div class="flex flex-col w-full space-y-2">
          <div
            v-for="(stock, i) in stocks"
            :key="i"
            @click="jumpToStock(stock.id ?? 0)"
            class="p-2 hover:bg-blue-500/25 rounded-xl transition-colors duration-150 cursor-pointer"
          >
            <div>
              <label-base size="sm" color="stone" class="block font-mono">{{
                stock.ref
              }}</label-base>
              <label-base class="block font-bold font-mono">{{ stock.name }}</label-base>
              <label-base size="sm" :color="colorStockType[stock.type ?? 0]" class="block"
                >{{ stock.type === 1 ? 'In' : 'Out' }}
              </label-base>
            </div>
          </div>
          <div class="text-center">
            <label-base size="xs" color="stone"
              >Hanya menampilkan 5 data saja, silakan eja dengan benar</label-base
            >
          </div>
        </div>
      </card-info>
    </div>

    <div
      v-if="loadStatus === LoadStatus.NotFound || loadStatus === LoadStatus.Error"
      class="text-center pt-8 px-4"
    >
      <card-error :type="loadStatus === LoadStatus.NotFound ? 'notFound' : 'error'"></card-error>
    </div>
  </div>
</template>

<style scoped></style>
